import React, {useEffect, useMemo, useState} from "react"
import {Box, Checkbox, Typography, Button, Tooltip, IconButton, Dialog} from "@mui/material"
import {IndeterminateCheckBox} from "@mui/icons-material"
import InfoIcon from "@mui/icons-material/Info"
import CancelIcon from "@mui/icons-material/CancelTwoTone"
import ReplaceIcon from "@mui/icons-material/AutorenewTwoTone"
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket"
import RefreshIcon from "@mui/icons-material/Refresh"
import {LineItem, Payment} from "../../dtos/orderResponse"
import {ButtonActionTypes, postActionLog} from "../../services/order/TrackOrderActions"
//import {Cancel} from "../order/lineItem/lineItemActions/Cancel"
import {UpdateDocument} from "../order/lineItem/lineItemActions/UpdateDocument"
import {AddToCart} from "../order/lineItem/lineItemActions/AddToCart"
//import {Replace} from "../order/lineItem/lineItemActions/Replace"
import {OrderCredits} from "../../services/order/CreditDetails"
import {Event} from "../../../src/dtos/eventResponse"
import {getCaseId} from "../../utils/UrlParser"
import {v4 as uuidv4} from "uuid"
import MoneyOffIcon from "@mui/icons-material/MoneyOffTwoTone"
//import MultiSelectRefund from "../order/lineItem/lineItemActions/MultiSelectRefundAndCredit/MultiSelectRefund/MultiSelectRefund"
//import MultiSelectCredit from "../order/lineItem/lineItemActions/MultiSelectRefundAndCredit/MultiSelectCredit/MultiSelectCreditWrapper"
import AlertWithStatus from "../feedback/AlertWithStatus"
import useAlert from "../order/lineItem/lineItemActions/MultiSelectRefundAndCredit/MultiSelectRefund/orchestrator/alert.logic"
import {OrderActionTrigger} from "@commercehub/order"
import {CREDIT_DISABLED_TOOLTIP, RESTRICTED_PAYMENT} from "../../utils/paymentCreditRestrictions"
import TooltipLabel from "../toolTip/TooltipLabel"

interface MultiSelectBarProps {
	onMainCheckboxClick: (checked: boolean) => void
	removeLineItem: (lineItem: LineItem, action: any) => void
	customerId: string
	tenant: string
	selectedItems: LineItem[]
	setSelectedItems: React.Dispatch<React.SetStateAction<LineItem[]>>
	reloadParent: any
	isVCSOrder: boolean
	orderIsLegacy: boolean
	cartId: string
	orderIsReleasedToFulfillment: boolean
	isVCSAgent: boolean
	orderNumber: string
	orderCredits: OrderCredits | null
	events: Event[]
	culture: string
	defaultCountryCode: string
	isPayOnBillingAccountOrder: boolean
	isCompanyCardOrder: boolean
	isFullyPaidWithMC: boolean
	paymentFullySuccessful: boolean
	isAnonymousCustomer: boolean
	orderPaymentsData: Payment[]
}

const MultiSelectBar: React.FC<MultiSelectBarProps> = ({
	onMainCheckboxClick,
	removeLineItem,
	customerId,
	tenant,
	selectedItems,
	setSelectedItems,
	reloadParent,
	isVCSOrder,
	orderIsLegacy,
	cartId,
	orderIsReleasedToFulfillment,
	isVCSAgent,
	orderNumber,
	orderCredits,
	events,
	culture,
	defaultCountryCode,
	isPayOnBillingAccountOrder,
	isCompanyCardOrder,
	isFullyPaidWithMC,
	paymentFullySuccessful,
	isAnonymousCustomer,
	orderPaymentsData,
}) => {
	const [cancelOpen, setCancelOpen] = useState(false)
	const [replaceOpen, setReplaceOpen] = useState(false)
	const [addToCart, setAddToCart] = useState(false)
	const [updateDocumentOpen, setUpdateDocumentOpen] = useState(false)
	const [refundItemsOpen, setRefundItemsOpenOpen] = useState(false)
	const [creditItemsOpen, setCreditItemsOpen] = useState(false)
	const {state: alert, open: openAlert, close: closeAlert} = useAlert()
	useEffect(() => {
		if (selectedItems.length === 0) {
			setRefundItemsOpenOpen(false)
			setCreditItemsOpen(false)
			setUpdateDocumentOpen(false)
			setCancelOpen(false)
			setAddToCart(false)
			setReplaceOpen(false)
		}
	}, [selectedItems])

	const handleChange = (event: any) => {
		onMainCheckboxClick(event.target.checked)
	}
	const isChecked = selectedItems.length > 0
	const multiSelectPermission = !isVCSAgent && isVCSOrder
	const caseId = getCaseId()

	const logActionToSnowflake = (action: string, lineItems: LineItem[]) => {
		const id = uuidv4()
		lineItems.forEach((lineItem: LineItem) => {
			postActionLog(
				{
					event: action,
					actionMetaData: {
						orderNumber: orderNumber,
						lineItemId: lineItem.id,
						buttonClickId: id,
					},
				},
				caseId
			)
		})
	}

	const openCancelItemsDialog = () => {
		let filteredItems = selectedItems.filter((item) => item.isCancellable)
		logActionToSnowflake(ButtonActionTypes.ClickCancelMultiSelectItemsButton, filteredItems)
		setCancelOpen(true)
	}

	const openAddToCartDialog = () => {
		logActionToSnowflake(ButtonActionTypes.ClickAddToCartMultiSelectButton, selectedItems)
		setAddToCart(true)
	}

	const openReplaceItemDialog = () => {
		const filteredItems = selectedItems.filter(
			(lineItem) =>
				!(
					lineItem.customOrderData &&
					lineItem.customOrderData.order &&
					lineItem.customOrderData.order.postOrderActionOverrides &&
					lineItem.customOrderData.order.postOrderActionOverrides.replacement &&
					lineItem.customOrderData.order.postOrderActionOverrides.replacement.disabled === true
				)
		)
		logActionToSnowflake(ButtonActionTypes.ClickReplaceMultiSelectItemsButton, filteredItems)
		setReplaceOpen(true)
	}
	const openUpdateDocumentDialog = () => {
		logActionToSnowflake(ButtonActionTypes.ClickUpdateDocumentMultiSelectButton, selectedItems)
		setUpdateDocumentOpen(true)
	}

	const openRefundItemsDialog = () => {
		logActionToSnowflake(ButtonActionTypes.ClickMultiSelectRefundButton, selectedItems)
		setRefundItemsOpenOpen(true)
	}

	const openCreditItemsDialog = () => {
		// TODO: update the snowflake event
		logActionToSnowflake(ButtonActionTypes.ClickMultiSelectCreditButton, selectedItems)
		setCreditItemsOpen(true)
	}

	const isNonCancelableItems = () => {
		if (selectedItems && selectedItems.length > 0) {
			return selectedItems.every((lineItem) => lineItem.isCancellable === false)
		}
		return false
	}

	function getReplacementHint() {
		if (orderIsLegacy) {
			return "This is a legacy order, replacements are not allowed."
		} else if (!orderIsReleasedToFulfillment) {
			return "Replacements are not allowed on this order where a payment has not been successfully processed. Please check payment status."
		} else if (
			selectedItems &&
			selectedItems.length !== 0 &&
			selectedItems.every(
				(lineItem) =>
					lineItem.customOrderData &&
					lineItem.customOrderData.order &&
					lineItem.customOrderData.order.postOrderActionOverrides &&
					lineItem.customOrderData.order.postOrderActionOverrides.replacement &&
					lineItem.customOrderData.order.postOrderActionOverrides.replacement.disabled === true
			)
		) {
			return "Replacements are not allowed on this item; product is temporarily unavailable."
		}
		return ""
	}

	// const toBlockRefundItemsButton = useMemo(() => {
	// 	const blockList: {[lineItemsId: string]: boolean} = {}
	// 	selectedItems.forEach((lineItem) => {
	// 		const res =
	// 			(isVCSOrder && (!isVCSAgent || isPayOnBillingAccountOrder)) ||
	// 			orderIsLegacy ||
	// 			lineItem.refundableAmounts.total === 0 ||
	// 			isFullyPaidWithMC ||
	// 			!paymentFullySuccessful

	// 		blockList[lineItem.id] = res
	// 	})
	// 	return blockList
	// }, [
	// 	isFullyPaidWithMC,
	// 	isPayOnBillingAccountOrder,
	// 	isVCSAgent,
	// 	isVCSOrder,
	// 	orderIsLegacy,
	// 	paymentFullySuccessful,
	// 	selectedItems,
	// ])

	// const toBlockCreditItemsButton = useMemo(() => {
	// 	const blockList: {[lineItemsId: string]: boolean} = {}
	// 	selectedItems.forEach((lineItem) => {
	// 		const res =
	// 			(isVCSOrder && (!isVCSAgent || isPayOnBillingAccountOrder || isCompanyCardOrder)) ||
	// 			isAnonymousCustomer

	// 		blockList[lineItem.id] = res
	// 	})
	// 	return blockList
	// }, [
	// 	isPayOnBillingAccountOrder,
	// 	isCompanyCardOrder,
	// 	isVCSAgent,
	// 	isVCSOrder,
	// 	selectedItems,
	// 	isAnonymousCustomer,
	// ])

	const isNonReplacableItems = () => {
		if (orderIsLegacy) {
			return true
		}
		if (!orderIsReleasedToFulfillment) {
			return true
		}
		if (isVCSOrder && !isVCSAgent) {
			return true
		}
		return (
			selectedItems &&
			selectedItems.length !== 0 &&
			selectedItems.every(
				(lineItem) =>
					lineItem.customOrderData &&
					lineItem.customOrderData.order &&
					lineItem.customOrderData.order.postOrderActionOverrides &&
					lineItem.customOrderData.order.postOrderActionOverrides.replacement &&
					lineItem.customOrderData.order.postOrderActionOverrides.replacement.disabled === true
			)
		)
	}
	const isNonUpdatableItems = () => {
		if (orderIsLegacy) {
			return true
		}
		if (isVCSOrder && !isVCSAgent) {
			return true
		}
		return (
			selectedItems &&
			selectedItems.length !== 0 &&
			selectedItems.every(
				(lineItem) =>
					!lineItem.editDocumentUrl ||
					lineItem.editDocumentUrl === "" ||
					lineItem.editDocumentUrl.includes("/albelli")
			)
		)
	}
	const onCompleteAction = () => {
		reloadParent()
		setSelectedItems([])
	}
	return (
		<>
			{alert.type && (
				<AlertWithStatus
					open={alert.open}
					status={alert.type}
					message={alert.message}
					closeAlert={closeAlert}
				/>
			)}
			{/* <Cancel
				customerId={customerId}
				orderNumber={orderNumber}
				open={cancelOpen}
				removeLineItem={removeLineItem}
				reloadParent={() => {
					reloadParent()
					onMainCheckboxClick(false)
				}}
				close={() => {
					setCancelOpen(false)
				}}
				lineItems={selectedItems}
				isMultiSelect
			/> */}
			<OrderActionTrigger
				type="cancel-items"
				orderId={orderNumber}
				isOpen={cancelOpen}
				onOpenChange={(isOpen: boolean) => setCancelOpen(isOpen)}
				onComplete={onCompleteAction}
				selectedItemIds={selectedItems.map((item) => item.id)}
				onItemRemove={(updatedIds) => {
					setSelectedItems(
						updatedIds
							.map((id) => selectedItems.find((item) => item.id === id))
							.filter(Boolean) as LineItem[]
					)
				}}
			/>
			{/* <AddToCart
				tenantName={tenant}
				customerId={customerId}
				orderNumber={orderNumber}
				lineItems={selectedItems}
				open={addToCart}
				removeLineItem={removeLineItem}
				reloadParent={() => {
					reloadParent()
					onMainCheckboxClick(false)
				}}
				isMultiSelect
				close={() => {
					setAddToCart(false)
				}}
			/> */}
			<OrderActionTrigger
				type="add-to-cart"
				isOpen={addToCart}
				onOpenChange={setAddToCart}
				onComplete={onCompleteAction}
				orderId={orderNumber}
				selectedItemIds={Array.from(selectedItems.map((item) => item.id))}
				shopperId={customerId}
				onItemRemove={(updatedIds) => {
					setSelectedItems(
						updatedIds
							.map((id) => selectedItems.find((item) => item.id === id))
							.filter(Boolean) as LineItem[]
					)
				}}
			/>

			<OrderActionTrigger
				type="replace-items"
				orderId={orderNumber}
				isOpen={replaceOpen}
				onOpenChange={(isOpen: boolean) => setReplaceOpen(isOpen)}
				onComplete={onCompleteAction}
				selectedItemIds={selectedItems.map((item) => item.id)}
				onItemRemove={(updatedIds) => {
					setSelectedItems(
						updatedIds
							.map((id) => selectedItems.find((item) => item.id === id))
							.filter(Boolean) as LineItem[]
					)
				}}
			/>
			{/* <Replace
				customerId={customerId}
				orderId={orderNumber}
				open={replaceOpen}
				removeLineItem={removeLineItem}
				reloadParent={() => {
					reloadParent()
					onMainCheckboxClick(false)
				}}
				close={() => {
					setReplaceOpen(false)
				}}
				lineItems={selectedItems}
				orderCredits={orderCredits}
				locale={culture}
				events={events}
				isMultiSelect
			/> */}

			{/* <UpdateDocument
				orderNumber={orderNumber}
				open={updateDocumentOpen}
				reloadParent={() => {
					reloadParent()
					onMainCheckboxClick(false)
				}}
				customerId={customerId}
				removeLineItem={removeLineItem}
				lineItems={selectedItems}
				tenantName={tenant}
				isMultiSelect
				close={() => {
					setUpdateDocumentOpen(false)
				}}
				locale={culture}
				defaultCountryCode={defaultCountryCode}
				isVCSagent={isVCSAgent}
				orderIsLegacy={orderIsLegacy}
				isVCSorder={isVCSOrder}
			/> */}
			<OrderActionTrigger
				type="update-document-items"
				isOpen={updateDocumentOpen}
				onOpenChange={setUpdateDocumentOpen}
				onComplete={onCompleteAction}
				orderId={orderNumber}
				selectedItemIds={Array.from(selectedItems.map((item) => item.id))}
				shopperId={customerId}
				onItemRemove={(updatedIds) => {
					setSelectedItems(
						updatedIds
							.map((id) => selectedItems.find((item) => item.id === id))
							.filter(Boolean) as LineItem[]
					)
				}}
			/>
			{/* <Dialog
				open={refundItemsOpen}
				onClose={() => {
					setRefundItemsOpenOpen(false)
				}}
			>
				<MultiSelectRefund
					orderNumber={orderNumber}
					lineItems={selectedItems}
					orderCredits={orderCredits}
					blockedLineItemList={toBlockRefundItemsButton}
					events={events}
					close={() => {
						setRefundItemsOpenOpen(false)
					}}
					reloadParent={() => {
						reloadParent()
						onMainCheckboxClick(false)
					}}
					orderPaymentsData={orderPaymentsData}
				/>
			</Dialog> */}
			<OrderActionTrigger
				type="refund-items"
				orderId={orderNumber}
				isOpen={refundItemsOpen}
				onOpenChange={(isOpen: boolean) => setRefundItemsOpenOpen(isOpen)}
				onComplete={onCompleteAction}
				selectedItemIds={Array.from(selectedItems.map((item) => item.id))}
				onItemRemove={(updatedIds) => {
					setSelectedItems(
						updatedIds
							.map((id) => selectedItems.find((item) => item.id === id))
							.filter(Boolean) as LineItem[]
					)
				}}
			/>
			{/* <Dialog
				open={creditItemsOpen}
				onClose={() => {
					setCreditItemsOpen(false)
				}}
			>
				<MultiSelectCredit
					openAlert={openAlert}
					orderCredits={orderCredits}
					orderNumber={orderNumber}
					blockedLineItemList={toBlockCreditItemsButton}
					lineItems={selectedItems}
					events={events}
					close={() => {
						setCreditItemsOpen(false)
					}}
					reloadParent={() => {
						reloadParent()
						onMainCheckboxClick(false)
					}}
					orderPaymentsData={orderPaymentsData}
				/>
			</Dialog> */}
			<OrderActionTrigger
				type="credit-items"
				orderId={orderNumber}
				isOpen={creditItemsOpen}
				onOpenChange={(isOpen: boolean) => setCreditItemsOpen(isOpen)}
				onComplete={onCompleteAction}
				selectedItemIds={Array.from(selectedItems.map((item) => item.id))}
				onItemRemove={(updatedIds) => {
					setSelectedItems(
						updatedIds
							.map((id) => selectedItems.find((item) => item.id === id))
							.filter(Boolean) as LineItem[]
					)
				}}
			/>
			{!multiSelectPermission && (
				<Box
					style={{
						width: "fit-content",
						padding: "12px 12px",
						background: selectedItems.length > 0 ? "#EBF6FF" : "transparent",
						borderRadius: 8,
						overflow: "hidden",
						display: "flex",
						alignItems: "center",
						marginBottom: 16,
						gap: 16,
					}}
				>
					<Box style={{display: "flex", alignItems: "center", gap: 8}}>
						<Checkbox
							checked={isChecked}
							checkedIcon={<IndeterminateCheckBox style={{color: "#2196F3"}} />}
							onClick={handleChange}
						/>
						<Typography
							style={{
								color: "black",
								fontSize: 16,
								fontWeight: 400,
								lineHeight: "24px",
								letterSpacing: "0.15px",
							}}
						>
							{selectedItems.length} {selectedItems.length > 1 ? "items" : "item"} selected
						</Typography>
					</Box>
					<Box style={{display: "flex", alignItems: "center", gap: 8}}>
						<Button
							style={{
								padding: "4px 5px",
								borderRadius: 4,
								display: "flex",
								alignItems: "center",
								textTransform: "uppercase",
								fontSize: 13,
								fontWeight: 500,
								letterSpacing: "0.46px",
							}}
							onClick={openCancelItemsDialog}
							disabled={
								selectedItems.length === 0 ||
								(isVCSOrder && !isVCSAgent) ||
								orderIsLegacy ||
								isNonCancelableItems()
							}
							color="primary"
							startIcon={<CancelIcon />}
						>
							Cancel Items
						</Button>

						<Button
							style={{
								padding: "4px 5px",
								borderRadius: 4,
								display: "flex",
								alignItems: "center",
								textTransform: "uppercase",
								fontSize: 13,
								fontWeight: 500,
								letterSpacing: "0.46px",
							}}
							onClick={openReplaceItemDialog}
							disabled={selectedItems.length === 0 || isNonReplacableItems()}
							color="primary"
							startIcon={<ReplaceIcon />}
						>
							Replace Items
						</Button>
						{isNonReplacableItems() && (
							<Tooltip title={getReplacementHint()} disableHoverListener={false}>
								<IconButton aria-label="info" style={{paddingLeft: "unset"}}>
									<InfoIcon />
								</IconButton>
							</Tooltip>
						)}

						<Button
							style={{
								padding: "4px 5px",
								borderRadius: 4,
								display: "flex",
								alignItems: "center",
								textTransform: "uppercase",
								fontSize: 13,
								fontWeight: 500,
								letterSpacing: "0.46px",
							}}
							onClick={openAddToCartDialog}
							disabled={selectedItems.length === 0 || (isVCSOrder && !isVCSAgent) || !cartId}
							color="primary"
							startIcon={<ShoppingBasketIcon />}
						>
							Add To Cart
						</Button>
						<Button
							style={{
								padding: "4px 5px",
								borderRadius: 4,
								display: "flex",
								alignItems: "center",
								textTransform: "uppercase",
								fontSize: 13,
								fontWeight: 500,
								letterSpacing: "0.46px",
							}}
							onClick={openUpdateDocumentDialog}
							disabled={selectedItems.length === 0 || isNonUpdatableItems()}
							color="primary"
							startIcon={<RefreshIcon />}
						>
							Update Documents
						</Button>
						<Box sx={{display: "flex"}}>
							<Button
								style={{
									padding: "4px 5px",
									borderRadius: 4,
									display: "flex",
									alignItems: "center",
									textTransform: "uppercase",
									fontSize: 13,
									fontWeight: 500,
									letterSpacing: "0.46px",
								}}
								onClick={openRefundItemsDialog}
								disabled={
									selectedItems.length === 0 ||
									(!paymentFullySuccessful &&
										!orderPaymentsData.some((p) => p.paymentMethod in RESTRICTED_PAYMENT))
								}
								color="primary"
								startIcon={<MoneyOffIcon />}
							>
								Refund Items
							</Button>
							{selectedItems.length === 0 ||
								(!paymentFullySuccessful &&
									!orderPaymentsData.some((p) => p.paymentMethod in RESTRICTED_PAYMENT) && (
										<TooltipLabel
											labelTitle=""
											toolTipHintText={CREDIT_DISABLED_TOOLTIP}
										></TooltipLabel>
									))}
						</Box>
						<Box sx={{display: "flex"}}>
							<Button
								style={{
									padding: "4px 5px",
									borderRadius: 4,
									display: "flex",
									alignItems: "center",
									textTransform: "uppercase",
									fontSize: 13,
									fontWeight: 500,
									letterSpacing: "0.46px",
								}}
								onClick={openCreditItemsDialog}
								// TODO:959 update disabled logic
								disabled={
									selectedItems.length === 0 ||
									orderPaymentsData.some((p) => p.paymentMethod in RESTRICTED_PAYMENT)
								}
								color="primary"
								startIcon={<MoneyOffIcon />}
							>
								Credit Items
							</Button>
							{selectedItems.length > 0 &&
								orderPaymentsData.some((p) => p.paymentMethod in RESTRICTED_PAYMENT) && (
									<TooltipLabel
										labelTitle=""
										toolTipHintText={CREDIT_DISABLED_TOOLTIP}
									></TooltipLabel>
								)}
						</Box>
					</Box>
				</Box>
			)}
		</>
	)
}

export default MultiSelectBar
